import React, { useCallback, useEffect, useState } from 'react';
import { ADMIN_INTERFACES, SUPERADMIN_KEY } from '../shared/adminInterfaces';
import { Icon, Modal, Notice } from '../exchanges/components';
import { formatDateTime } from './api';

const ACTION_LABELS = {
  'access.permissions': 'Droits modifiés',
  'access.create': 'Compte créé',
  'access.reset': 'Accès réinitialisé',
  'access.disable': 'Compte désactivé',
  'access.enable': 'Compte réactivé',
  'salesforce.mapping': 'Correspondance Salesforce enregistrée'
};

const interfaceLabel = (key) => ADMIN_INTERFACES.find((item) => item.key === key)?.label || key;

function auditSummary(entry) {
  const d = entry.detail || {};
  if (entry.action === 'salesforce.mapping') return `Profil ${d.profile}, révision ${d.revision} (${d.after} ligne(s))`;
  if (entry.action === 'access.permissions') return `${d.target} : ${(d.after || []).map(interfaceLabel).join(', ') || 'aucun droit'}`;
  if (entry.action === 'access.create') return `${d.target} : ${(d.permissions || []).map(interfaceLabel).join(', ') || 'aucun droit'}`;
  return d.target || '';
}

export default function AccessManagement({ api }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [creating, setCreating] = useState(false);
  const [secrets, setSecrets] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [audit, setAudit] = useState(null);

  const refresh = useCallback(() => api('/access/users').then((data) => setUsers(data.users)), [api]);
  useEffect(() => { refresh().catch((e) => setError(e.message)).finally(() => setLoading(false)); }, [refresh]);

  async function toggle(user, key, checked) {
    const next = checked ? [...new Set([...user.permissions, key])] : user.permissions.filter((item) => item !== key);
    setBusy(user.id); setError(''); setMessage('');
    try {
      await api(`/access/users/${user.id}/permissions`, { method: 'PUT', body: { permissions: next } });
      setUsers((previous) => previous.map((u) => (u.id === user.id ? { ...u, permissions: next } : u)));
      setMessage(`Droits de ${user.name} mis à jour.`);
    } catch (e) { setError(e.message); }
    finally { setBusy(''); }
  }

  async function runConfirmed() {
    const { user, kind } = confirm;
    setBusy(user.id); setError(''); setMessage('');
    try {
      if (kind === 'reset') {
        const result = await api(`/access/users/${user.id}/reset`, { method: 'POST', body: {} });
        setSecrets({ ...result, reason: 'reset' });
      } else {
        await api(`/access/users/${user.id}/${kind}`, { method: 'POST', body: {} });
        setMessage(kind === 'disable' ? `Le compte de ${user.name} est désactivé et ses sessions sont fermées.` : `Le compte de ${user.name} est réactivé.`);
      }
      setConfirm(null);
      await refresh();
    } catch (e) { setError(e.message); setConfirm(null); }
    finally { setBusy(''); }
  }

  async function loadAudit() {
    try { setAudit((await api('/access/audit')).entries); }
    catch (e) { setError(e.message); }
  }

  return <>
    <div className="ex-page-heading"><div><p className="ex-eyebrow">ADMINISTRATION DU PORTAIL</p><h1>Gestion des accès</h1><p className="ex-muted">Créez les comptes administratifs et choisissez les interfaces ouvertes à chacun.</p></div>
      <div className="ex-actions"><button className="ex-button ex-button--primary" onClick={() => { setCreating(true); setError(''); }}><Icon name="plus" size={17} />Créer un compte</button></div>
    </div>
    <Notice>{error}</Notice><Notice type="success">{message}</Notice>
    <Notice type="info">Le droit « {interfaceLabel(SUPERADMIN_KEY)} » fait du compte un administrateur : il ouvre toutes les interfaces et permet de gérer les comptes. Chaque personne se connecte avec son mot de passe puis sa propre clé d’accès.</Notice>

    <section className="ex-list-card">
      {loading ? <div className="ex-empty" role="status"><h3>Chargement des comptes…</h3></div>
        : <div className="ex-table-scroll"><table className="ad-access-table">
          <caption className="ex-sr-only">Comptes administratifs et droits</caption>
          <thead><tr><th scope="col">Compte</th><th scope="col">État</th>{ADMIN_INTERFACES.map((item) => <th key={item.key} scope="col" className="ad-center">{item.label}</th>)}<th scope="col" className="ex-dossier-action-heading">Actions</th></tr></thead>
          <tbody>{users.map((user) => {
            const isAdmin = user.permissions.includes(SUPERADMIN_KEY);
            return <tr key={user.id} className={user.enabled ? '' : 'is-disabled'}>
              <td><div className="ad-cell-source"><span className="ex-avatar">{user.name.charAt(0)}</span><span><strong>{user.name}{user.self && <span className="ad-badge ad-badge--muted">Vous</span>}</strong><small>{user.username}</small></span></div></td>
              <td><div className="ad-state">
                {!user.enabled ? <span className="ad-badge ad-badge--error">Désactivé</span>
                  : user.keyCount ? <span className="ad-badge ad-badge--ok">{user.keyCount} clé(s) d’accès</span>
                    : user.activationPending ? <span className="ad-badge ad-badge--warn">Activation en attente</span>
                      : <span className="ad-badge ad-badge--error">Activation expirée</span>}
                <small>Dernière activité : {formatDateTime(user.lastSeenAt)}</small>
              </div></td>
              {ADMIN_INTERFACES.map((item) => {
                const included = isAdmin && item.key !== SUPERADMIN_KEY;
                const locked = user.self && item.key === SUPERADMIN_KEY;
                return <td key={item.key} className="ad-center"><input type="checkbox"
                  aria-label={`${item.label} pour ${user.name}`}
                  title={included ? 'Inclus dans le droit administrateur' : locked ? 'Vous ne pouvez pas retirer votre propre droit administrateur' : undefined}
                  checked={included || user.permissions.includes(item.key)}
                  disabled={busy === user.id || included || locked}
                  onChange={(e) => toggle(user, item.key, e.target.checked)} /></td>;
              })}
              <td className="ex-dossier-action-cell"><div className="ex-row-actions">
                {!user.self && <button className="ex-button" disabled={busy === user.id} onClick={() => setConfirm({ user, kind: 'reset' })}>Réinitialiser l’accès</button>}
                {!user.self && (user.enabled
                  ? <button className="ex-button ex-button--danger" disabled={busy === user.id} onClick={() => setConfirm({ user, kind: 'disable' })}>Désactiver</button>
                  : <button className="ex-button" disabled={busy === user.id} onClick={() => setConfirm({ user, kind: 'enable' })}>Réactiver</button>)}
              </div></td>
            </tr>;
          })}</tbody>
        </table></div>}
    </section>

    <details className="ad-panel" onToggle={(e) => { if (e.currentTarget.open && !audit) loadAudit(); }}>
      <summary>Journal des modifications</summary>
      {!audit ? <p className="ex-help" role="status">Chargement…</p>
        : !audit.length ? <p className="ex-empty-small">Aucune modification enregistrée.</p>
          : <ul className="ad-audit-list">{audit.map((entry) => <li key={entry.id}><span className="ad-audit-date">{formatDateTime(entry.at)}</span><strong>{ACTION_LABELS[entry.action] || entry.action}</strong><span>{auditSummary(entry)}</span><small>par {entry.by}</small></li>)}</ul>}
    </details>

    {creating && <CreateDialog api={api} onClose={() => setCreating(false)} onCreated={async (result) => { setCreating(false); setSecrets({ ...result, reason: 'create' }); await refresh(); }} />}
    {secrets && <SecretsDialog secrets={secrets} onClose={() => setSecrets(null)} />}
    {confirm && <Modal title={confirm.kind === 'reset' ? 'Réinitialiser l’accès' : confirm.kind === 'disable' ? 'Désactiver le compte' : 'Réactiver le compte'} busy={Boolean(busy)} onClose={() => setConfirm(null)}>
      <p>{confirm.kind === 'reset'
        ? `Un nouveau mot de passe et un code d’activation seront générés pour ${confirm.user.name}. Ses clés d’accès et ses sessions seront supprimées : il devra enregistrer une nouvelle clé.`
        : confirm.kind === 'disable'
          ? `${confirm.user.name} ne pourra plus se connecter et ses sessions ouvertes seront fermées. Ses droits sont conservés.`
          : `${confirm.user.name} pourra de nouveau se connecter avec ses clés d’accès.`}</p>
      <div className="ex-modal-footer">
        <button className="ex-button" disabled={Boolean(busy)} onClick={() => setConfirm(null)}>Annuler</button>
        <button className={`ex-button ${confirm.kind === 'disable' ? 'ex-button--danger-confirm' : 'ex-button--primary'}`} disabled={Boolean(busy)} onClick={runConfirmed}>Confirmer</button>
      </div>
    </Modal>}
  </>;
}

function CreateDialog({ api, onClose, onCreated }) {
  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [permissions, setPermissions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function submit(event) {
    event.preventDefault(); setBusy(true); setError('');
    try { onCreated(await api('/access/users', { method: 'POST', body: { username, name, permissions } })); }
    catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }
  return <Modal title="Créer un compte administratif" busy={busy} onClose={onClose}>
    <Notice>{error}</Notice>
    <form className="ex-form" onSubmit={submit}>
      <div className="ex-identity-fields">
        <label>Identifiant<input required autoComplete="off" maxLength={190} value={username} onChange={(e) => setUsername(e.target.value.toLowerCase())} placeholder="prenom.nom" pattern="[a-z0-9._@+\-]{3,190}" /><small>Lettres minuscules, chiffres, . _ @ + -</small></label>
        <label>Nom affiché<input required maxLength={190} value={name} onChange={(e) => setName(e.target.value)} placeholder="Prénom Nom" /></label>
      </div>
      <fieldset><legend className="ad-select-label">Interfaces ouvertes</legend>
        {ADMIN_INTERFACES.map((item) => <label key={item.key} className="ex-checkbox"><input type="checkbox" checked={permissions.includes(item.key)} onChange={(e) => setPermissions((previous) => (e.target.checked ? [...previous, item.key] : previous.filter((k) => k !== item.key)))} />{item.label}{item.superadmin ? ' (administrateur : toutes les interfaces)' : ''}</label>)}
      </fieldset>
      <p className="ex-help">Un mot de passe provisoire et un code d’activation (valable 24 heures) seront affichés une seule fois. Cette action nécessite une connexion datant de moins de dix minutes.</p>
      <div className="ex-modal-footer"><button type="button" className="ex-button" disabled={busy} onClick={onClose}>Annuler</button><button className="ex-button ex-button--primary" disabled={busy}>{busy ? 'Création…' : 'Créer le compte'}</button></div>
    </form>
  </Modal>;
}

function SecretsDialog({ secrets, onClose }) {
  const [copied, setCopied] = useState('');
  async function copy(label, value) {
    try { await navigator.clipboard.writeText(value); setCopied(label); } catch { setCopied(''); }
  }
  const rows = [['Identifiant', secrets.user.username], ['Mot de passe provisoire', secrets.password], ['Code d’activation', secrets.activationCode]];
  return <Modal title={secrets.reason === 'create' ? 'Compte créé' : 'Accès réinitialisé'} onClose={onClose}>
    <Notice type="info">Ces informations ne seront plus affichées. Transmettez le mot de passe et le code d’activation séparément, directement à {secrets.user.name}.</Notice>
    <dl className="ad-secrets">{rows.map(([label, value]) => <div key={label}><dt>{label}</dt><dd><code>{value}</code><button type="button" className="ex-icon-button" aria-label={`Copier : ${label}`} title="Copier" onClick={() => copy(label, value)}><Icon name={copied === label ? 'check' : 'copy'} size={16} /></button></dd></div>)}</dl>
    <p className="ex-help">Code valable jusqu’au {formatDateTime(secrets.expiresAt)}. À la première connexion sur /admin, cocher « Première connexion ou réactivation », saisir le code puis enregistrer une clé d’accès.</p>
    <div className="ex-modal-footer"><button className="ex-button ex-button--primary" onClick={onClose}>J’ai transmis ces informations</button></div>
  </Modal>;
}
